import {assert} from 'Asserts';
import {
  RegistrationTableRowData,
  SchemaInterfaceColumnTransformerBase,
} from 'notebook/integrations/registry/SchemaInterfaceColumnTransformerBase';
import {RegistrationTableMetadataQuery_complexPolymerSchemaConfiguration_GenericComplexPolymerSchemaConfigNode_OligoConjugateSchemaConfigNode_OligoDuplexSchemaConfigNode as ImplementedComplexPolymerInterface} from 'notebook/structured-tables/components/modals/__generated/RegistrationTableMetadataQuery';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {
  ComplexPolymerComponentFrontEndMetadata,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableColumnType} from 'notebook/tables/TableConstants';
import * as logging from 'util/Logging';

function isComplexPolymerInterface(
  implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
): implementedSchemaInterface is ImplementedComplexPolymerInterface {
  return [
    'GenericComplexPolymerSchemaConfigNode',
    'OligoConjugateSchemaConfigNode',
    'OligoDuplexSchemaConfigNode',
  ].includes(implementedSchemaInterface.__typename);
}
export default class ComplexPolymerRegTableColumnTransformerBase implements SchemaInterfaceColumnTransformerBase {
  replaceExistingColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    registeredTableRowMetadata: RegistrationTableRowData
  ): StructuredTableColumn | null {
    assert(
      isComplexPolymerInterface(implementedSchemaInterface),
      'Complex polymer transformer called with non-complex polymer interface'
    );
    // Only replace the column if the bioentity exists and the column is a tag value.
    if (registeredTableRowMetadata.bioentityExists || column.columnType !== TableColumnType.TAG_VALUE) {
      return null;
    }
    // Only replace the column if the column is a schema field for a complex polymer component (i.e. it's
    // specified in the SSI config).
    const complexPolymerSSIConfig = implementedSchemaInterface.configValues.find(
      (configValue) => configValue.schemaField.id === column.schemaFieldApiId
    );
    if (complexPolymerSSIConfig == null) {
      return null;
    }

    return {
      ...makeStructuredTableColumn({
        columnType: TableColumnType.COMPLEX_POLYMER_COMPONENT,
        name: column.name,
        isRequired: complexPolymerSSIConfig.isRequired,
        isReadOnly: false,
      }),
      columnType: TableColumnType.COMPLEX_POLYMER_COMPONENT,
      schemaFieldApiId: complexPolymerSSIConfig.schemaField.id,
    };
  }

  getNewColumns(
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ): Array<StructuredTableColumn> {
    assert(
      isComplexPolymerInterface(implementedSchemaInterface),
      'Complex polymer transformer called with non-complex polymer interface'
    );

    // Determine the column name based on schema type
    let columnName: string;
    if (implementedSchemaInterface.__typename === 'OligoConjugateSchemaConfigNode') {
      columnName = 'Oligo conjugate connections';
    } else if (implementedSchemaInterface.__typename === 'OligoDuplexSchemaConfigNode') {
      columnName = 'Oligo duplex connections';
    } else {
      columnName = 'Complex polymer connections'; // Generic complex polymer
    }

    // TODO (BNCH-100375): Add connections column when bioentity already exists - need a resolution path for
    // the connections first.
    return tableRowData.bioentityExists
      ? []
      : [
          makeStructuredTableColumn({
            columnType: TableColumnType.COMPLEX_POLYMER_CONNECTIONS,
            name: columnName,
            isReadOnly: false,
            isRequired: false,
          }),
        ];
  }

  shouldHideColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): boolean {
    // No columns should be hidden for complex polymers.
    return false;
  }

  getFrontEndMetadata(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): ComplexPolymerComponentFrontEndMetadata | null {
    assert(
      isComplexPolymerInterface(implementedSchemaInterface),
      'Complex polymer transformer called with non-complex polymer interface'
    );
    if (column.columnType !== TableColumnType.COMPLEX_POLYMER_COMPONENT) {
      return null;
    }
    const schemaConfig = implementedSchemaInterface.configValues.find(
      (configValue) => configValue.schemaField.id === column.schemaFieldApiId
    );
    // Some old templates were created before the table stored the schema config, so default to false in that
    // case.
    if (schemaConfig == null) {
      logging.warn('Schema config should be present for complex polymer component columns');
      return {allowsMultipleComponents: false};
    }
    return {allowsMultipleComponents: schemaConfig.allowsMultipleComponents};
  }
}
